import { BlipApi } from 'api/BlipApi';
import { BillingMetrics } from 'shared/BillingMetrics';
import { METHOD, RequestStatus } from 'shared/Requests';
import { useSubscriptionReferenceDate } from './BillingApi';
import { DailyMeuConsumption } from 'models/billing/meuConsumption/DailyMeuConsumption';
import { MonthlyMeuConsumption } from 'models/billing/meuConsumption/MonthlyMeuConsumption';
import { ConsolidatedMeuConsumption } from 'models/billing/meuConsumption/ConsolidatedMeuConsumption';

export default class BillingMeuConsumptionApi {
  private readonly BILLING_COMMAND_DESTINATION = 'postmaster@billing.blip.ai';
  private code = '';
  private status = '';
  private useSubscriptionReferenceDate: boolean | undefined = undefined;

  public async getUseSubscriptionReferenceDate(): Promise<void> {
    if (this.useSubscriptionReferenceDate === undefined) {
      this.useSubscriptionReferenceDate = await useSubscriptionReferenceDate();
    }
  }

  public getStatus() {
    return this.status;
  }

  public getCode() {
    return this.code;
  }

  public setSuccess() {
    this.status = RequestStatus.SUCCESS;
  }

  public setErrorLog(e) {
    const error = JSON.parse(<string>e);
    const {
      status,
      reason: { code },
    } = JSON.parse(error.message);
    this.code = code;
    this.status = status;
    console.log(e);
  }

  public async getConsolidatedMeuConsumption(
    tenantId: string,
    metricIds: string,
    month: string,
    storageType = 1,
  ): Promise<Array<ConsolidatedMeuConsumption>> {
    await this.getUseSubscriptionReferenceDate();
    const uri =
      `/tenants/${tenantId}/monthly-message-metrics/summary?month=${month}` +
      `&metricIdList=${metricIds}` +
      `&useSubscriptionReferenceDate=${this.useSubscriptionReferenceDate}` +
      `&storageType=${storageType}`;

    const command = {
      to: this.BILLING_COMMAND_DESTINATION,
      method: METHOD.GET,
      uri: uri,
    };

    try {
      const {
        response: { items },
      } = await BlipApi.sendCommand(command);
      this.setSuccess();
      return items.map((item) => new ConsolidatedMeuConsumption(item, item.metric.metricId));
    } catch (e) {
      this.setErrorLog(e);
      return new Array<ConsolidatedMeuConsumption>(
        new ConsolidatedMeuConsumption(null, BillingMetrics.monthlyEngagedUser),
        new ConsolidatedMeuConsumption(null, BillingMetrics.activeMessage),
      );
    }
  }

  public async getMonthlyMeuConsumption(
    tenantId: string,
    metricIds: string,
    month: string,
    registersToTake: number,
    storageType = 1,
  ): Promise<MonthlyMeuConsumption[]> {
    await this.getUseSubscriptionReferenceDate();
    let uri =
      `/tenants/${tenantId}/monthly-message-metrics/monthly/by-bot?month=${month}` +
      `&metricIdList=${metricIds}` +
      `&useSubscriptionReferenceDate=${this.useSubscriptionReferenceDate}`+
      `&storageType=${storageType}`;

    uri = uri + `&$take=${registersToTake}`;

    const command = {
      to: this.BILLING_COMMAND_DESTINATION,
      method: METHOD.GET,
      uri: uri,
    };

    try {
      const {
        response: { items },
      } = await BlipApi.sendCommand(command);
      this.setSuccess();
      return items.map((item) => new MonthlyMeuConsumption(item));
    } catch (e) {
      this.setErrorLog(e);
      return new Array<MonthlyMeuConsumption>();
    }
  }

  public async getDailyMeuConsumption(
    tenantId: string,
    metricIds: string,
    month: string,
    registersToTake: number,
    storageType = 1,
  ): Promise<DailyMeuConsumption[]> {
    await this.getUseSubscriptionReferenceDate();
    let uri =
      `/tenants/${tenantId}/monthly-message-metrics/daily?month=${month}` +
      `&metricIdList=${metricIds}` +
      `&useSubscriptionReferenceDate=${this.useSubscriptionReferenceDate}` +
      `&storageType=${storageType}`;

    uri = uri + `&$take=${registersToTake}`;

    const command = {
      to: this.BILLING_COMMAND_DESTINATION,
      method: METHOD.GET,
      uri: uri,
    };

    try {
      const {
        response: { items },
      } = await BlipApi.sendCommand(command);
      this.setSuccess();
      return items.map((item) => new DailyMeuConsumption(item));
    } catch (e) {
      this.setErrorLog(e);
      return new Array<DailyMeuConsumption>();
    }
  }
}